import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Set } from 'immutable';
import { combineLatest, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { TcsCustomPermission } from 'src/app/shared/authorization/permissions';
import { EntityType, UserRole } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import {
  ControlZoneProps,
  selectAllControlZoneEntities,
} from 'src/store/generated/control-zone-record.generated';
import { PermissionsService } from '../amg-authorization/permissions.service';
import { UserService } from '../tcs-identity/user.service';
import { AccessibleItemAuthorizationService } from './accessible-item-authorization.service';

@Injectable({ providedIn: 'root' })
export class ControlZoneAuthorizationService {
  constructor(
    private _user: UserService,
    private _accessibleItems: AccessibleItemAuthorizationService,
    private _permissions: PermissionsService,
    private _store: Store<AppState>,
  ) {}

  public isAuthorizedForCompany(companyId: string): Observable<boolean> {
    return this.getAuthorizedControlZones(companyId).pipe(
      map((czs) => czs.find((cz) => cz.companyId === companyId) != null),
    );
  }

  public getAuthorizedControlZones(companyId?: string): Observable<ControlZoneProps[]> {
    return this._user.user$.pipe(
      switchMap((user) => {
        if (user == null) {
          return of([]);
        }
        const accessibleItems$ = this._accessibleItems.getAccessibleItems();
        const controlZones$ = this._store
          .select(selectAllControlZoneEntities)
          .pipe(
            map((czs) =>
              czs.filter((cz) => companyId == null || companyId === cz.companyId).valueSeq(),
            ),
          );
        return combineLatest([accessibleItems$, controlZones$]).pipe(
          map(([accessibleItems, controlZones]) => {
            if (this._hasScopedPermission(user)) {
              // User has global permission to access all control zones
              return controlZones;
            }
            return controlZones.filter((cc) => {
              const companyAi = accessibleItems.find(
                (ai) => ai.entityId === cc.companyId && ai.type === EntityType.Company,
              );
              if (this._hasScopedPermission(companyAi)) {
                // User has been granted access to this control zone because
                // they have accesss to all control zones in this company
                return true;
              }
              const controlZoneAi = accessibleItems.find(
                (ai) => ai.entityId === cc.id && ai.type === EntityType.ControlZone,
              );
              if (this._hasScopedPermission(controlZoneAi)) {
                // User has been granted access to this specific control zone
                return true;
              }
              return false;
            });
          }),
          map((czs) => czs.toArray()),
        );
      }),
    );
  }

  private _hasScopedPermission(scope: { userRoles: Set<UserRole> | null } | undefined) {
    if (scope == null) {
      return false;
    }
    const { userRoles } = scope;
    if (userRoles == null) {
      return false;
    }
    return this._permissions.hasPermission(
      userRoles.toArray(),
      TcsCustomPermission.AccessControlZonesWithinScope,
    );
  }
}
